import React, { useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { buildResumePdfSections } from './ResumeDataInsertPage';

const ResumePdfPreviewPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pdfPreviewRef = useRef(null);

  const state = location.state || {};
  const resumePdfSections = buildResumePdfSections({
    formData: state.formData || {},
    technicalSections: state.technicalSections || [],
    workExperiences: state.workExperiences || [],
    projectEntries: state.projectEntries || [],
    educationEntries: state.educationEntries || [],
    languagesText: state.languagesText || '',
    certificationEntries: state.certificationEntries || [],
    achievementEntries: state.achievementEntries || [],
  });

  const generatedName = 'HEMANGI WAGH';
  const titleLine = 'Data Analyst';
  const contactInfo = ['+91 93225 88874', 'hema2901@gmail.com', 'Pune, Maharashtra'];
  const sectionHeadingStyle = {
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#1f5aa6',
    marginBottom: '8px',
    borderBottom: '1.5px solid #1f5aa6',
    paddingBottom: '4px',
    lineHeight: 1.3,
  };
  const sectionBodyStyle = {
    fontSize: '12px',
    lineHeight: '1.8',
    color: '#111111',
    whiteSpace: 'pre-wrap',
  };
  const boldLabelStyle = {
    fontWeight: 700,
    color: '#111111',
  };
  const bulletStyle = {
    margin: '0 0 4px 18px',
    paddingLeft: '4px',
    listStyle: 'disc',
    color: '#111111',
  };

  const formatContent = (content = '') => {
    const text = String(content || '').trim();
    if (!text) return null;

    const htmlLike = /<\s*(div|li|ul|p)\b/i.test(text);
    if (htmlLike) {
      try {
        const parser = new DOMParser();
        const document = parser.parseFromString(text, 'text/html');
        const htmlEntries = Array.from(document.querySelectorAll('li, div, p'))
          .map((node) => node.textContent?.replace(/\s+/g, ' ').trim())
          .filter(Boolean)
          .filter((value) => !/^\s*(?:&nbsp;|\s*)$/i.test(value));

        if (htmlEntries.length > 0) {
          return (
            <ul style={{ margin: 0, paddingLeft: '18px' }}>
              {htmlEntries.map((line, index) => (
                <li key={`${line}-${index}`} style={bulletStyle}>{line}</li>
              ))}
            </ul>
          );
        }
      } catch (error) {
        console.warn('Resume PDF HTML list parse failed:', error);
      }
    }

    const blocks = [];
    const lines = text
      .split(/\n+/)
      .map((line) => line.trim())
      .filter(Boolean);

    let currentBlock = [];
    const startsNewCompanyOrProject = (line) => /^(Company|Project|Certification|Achievement)\s*:/i.test(line);
    const isMetadataLine = (line) => /^(Company|Project|Role|Period|Certification|Achievement|Degree|University|Issuer|Grade|Location)\s*:/i.test(line);

    lines.forEach((line) => {
      const isBullet = /^[-•*]\s*/.test(line);

      if (startsNewCompanyOrProject(line) && currentBlock.length > 0) {
        blocks.push(currentBlock);
        currentBlock = [];
      }

      currentBlock.push(line);

      if (isBullet && currentBlock.length > 0 && !isMetadataLine(line)) {
        // Keep bullet content in the same block without creating a new group.
      }
    });

    if (currentBlock.length) {
      blocks.push(currentBlock);
    }

    if (blocks.length === 0) {
      return null;
    }

    return (
      <>
        {blocks.map((block, blockIndex) => {
          const metadata = block.filter((line) => isMetadataLine(line));
          const bulletLines = block
            .filter((line) => !isMetadataLine(line))
            .map((line) => line.replace(/^[-•*]\s*/, '').trim())
            .filter(Boolean);

          const formatMetadataLine = (line) => {
            const match = line.match(/^([A-Za-z\s]+):\s*(.*)$/);
            if (!match) {
              return <span style={{ fontWeight: 700 }}>{line}</span>;
            }
            const label = match[1].trim();
            const value = match[2].trim();
            return (
              <span style={{ fontWeight: 700 }}>
                {label}: {value}
              </span>
            );
          };

          return (
            <div key={`block-${blockIndex}`} style={{ marginBottom: blockIndex === blocks.length - 1 ? 0 : 10 }}>
              {metadata.length > 0 && (
                <div style={{ marginBottom: bulletLines.length ? 4 : 0 }}>
                  {metadata.map((line, lineIndex) => (
                    <div key={`${line}-${lineIndex}`} style={{ marginBottom: lineIndex === metadata.length - 1 ? 0 : 2 }}>
                      {formatMetadataLine(line)}
                    </div>
                  ))}
                </div>
              )}
              {bulletLines.length > 0 && (
                <ul style={{ margin: 0, paddingLeft: '18px' }}>
                  {bulletLines.map((line, bulletIndex) => (
                    <li key={`${line}-${bulletIndex}`} style={bulletStyle}>{line}</li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </>
    );
  };

  const handleDownloadPdf = async () => {
    if (!pdfPreviewRef.current) {
      return;
    }

    try {
      const element = pdfPreviewRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
      });

      const pdf = new jsPDF('p', 'pt', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const left = 40;
      const top = 38;
      const right = 40;
      const bottom = 50;
      const usableWidth = pageWidth - left - right;
      const usableHeight = pageHeight - top - bottom;
      const scale = usableWidth / canvas.width;

      let sourceY = 0;
      let pageIndex = 0;

      while (sourceY < canvas.height) {
        if (pageIndex > 0) {
          pdf.addPage();
        }

        const remainingPixels = canvas.height - sourceY;
        const slicePixels = Math.min(remainingPixels, Math.ceil(usableHeight / scale));
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = slicePixels;

        const context = sliceCanvas.getContext('2d');
        if (context) {
          context.fillStyle = '#ffffff';
          context.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
          context.drawImage(canvas, 0, sourceY, canvas.width, slicePixels, 0, 0, canvas.width, slicePixels);
        }

        const sliceImage = sliceCanvas.toDataURL('image/png');
        const sliceHeightPt = slicePixels * scale;
        pdf.addImage(sliceImage, 'PNG', left, top, usableWidth, sliceHeightPt);

        sourceY += slicePixels;
        pageIndex += 1;
      }

      const fileName = state.candidateId || state.resumeId ? `resume-${state.candidateId || state.resumeId}.pdf` : 'resume-draft.pdf';
      pdf.save(fileName);
    } catch (error) {
      console.error('Resume PDF generation failed:', error);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f3f4f6',
      padding: '32px 16px',
      boxSizing: 'border-box',
    }}>
      <div style={{ maxWidth: '1080px', margin: '0 auto' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '20px',
          flexWrap: 'wrap',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '28px', color: '#111827' }}>Resume PDF Preview</h2>
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/resume-update')}>Back to Edit</button>
            <button type="button" className="btn btn-primary" onClick={handleDownloadPdf}>Download PDF</button>
          </div>
        </div>

        <div
          ref={pdfPreviewRef}
          style={{
            width: '100%',
            maxWidth: '794px',
            minHeight: '1123px',
            margin: '0 auto',
            background: '#ffffff',
            color: '#111827',
            border: '1px solid #d1d5db',
            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.08)',
            padding: '42px 48px 90px',
            boxSizing: 'border-box',
            fontFamily: 'Arial, sans-serif',
          }}
        >
          <div style={{ marginBottom: '18px', paddingBottom: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '30px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', lineHeight: 1.2, color: '#111111' }}>
              {generatedName}
            </div>
            <div style={{ fontSize: '13px', color: '#111111', marginTop: '6px', lineHeight: 1.5, fontWeight: 500 }}>
              {titleLine}
            </div>
            <div style={{ fontSize: '12px', color: '#111111', marginTop: '4px', lineHeight: 1.6, fontWeight: 500 }}>
              {contactInfo.join(' | ')}
            </div>
          </div>

          {resumePdfSections.length === 0 ? (
            <div style={{ fontSize: '14px', color: '#111111' }}>No resume sections available.</div>
          ) : (
            resumePdfSections.map((section) => (
              <div key={section.title} style={{ marginBottom: '18px', pageBreakInside: 'avoid', breakInside: 'avoid', paddingBottom: '6px' }}>
                <div style={sectionHeadingStyle}>
                  {section.title}
                </div>
                <div style={sectionBodyStyle}>
                  {formatContent(section.content)}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ResumePdfPreviewPage;
